import { useEffect, useRef, useState } from "react";
import { MdMic, MdMicOff, MdStop } from "react-icons/md";
import { useTranslation } from "react-i18next";

interface CooldownConfig {
  /** Timestamp (Date.now()) when the current silence window started.
   *  Null disables the overlay. Updates to this prop reset the sweep. */
  triggerAt: number | null;
  /** Total duration the overlay should sweep over, in ms. */
  durationMs: number;
}

interface MicrophoneButtonProps {
  isListening: boolean;
  onStartListening: () => void;
  onStopListening: () => void;
  disabled?: boolean;
  isSupported: boolean;
  /** i18n key for tooltip when idle. Defaults to "voice.startRecording". */
  idleTooltipKey?: string;
  /** i18n key for tooltip when listening. Defaults to "voice.stopRecording". */
  listeningTooltipKey?: string;
  /** When provided, draws a WoW-style sweep overlay that drains clockwise
   *  from full to empty over durationMs. Useful for surfacing a silence-timeout
   *  countdown in conversation mode. */
  cooldown?: CooldownConfig;
}

const MicrophoneButton = ({
  isListening,
  onStartListening,
  onStopListening,
  disabled = false,
  isSupported,
  idleTooltipKey = "voice.startRecording",
  listeningTooltipKey = "voice.stopRecording",
  cooldown,
}: MicrophoneButtonProps) => {
  const { t } = useTranslation();

  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (!isListening || !cooldown?.triggerAt || cooldown.durationMs <= 0) {
      setProgress(0);
      return undefined;
    }
    const triggerAt = cooldown.triggerAt;
    const duration = cooldown.durationMs;
    const tick = () => {
      const elapsed = Date.now() - triggerAt;
      const p = Math.min(Math.max(elapsed / duration, 0), 1);
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isListening, cooldown?.triggerAt, cooldown?.durationMs]);

  const handleClick = () => {
    if (isListening) onStopListening();
    else onStartListening();
  };

  const tooltipTitle = !isSupported
    ? t("voice.notConfigured")
    : disabled
    ? t("voice.micDisabledWhileSpeaking")
    : isListening
    ? t(listeningTooltipKey)
    : t(idleTooltipKey);

  const baseClasses =
    "relative inline-flex items-center justify-center w-11 h-11 rounded-xl border-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed overflow-hidden";
  const stateClasses = isListening
    ? "bg-red-500 border-red-500 text-white hover:bg-red-600"
    : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200";

  const showCooldown = isListening && cooldown?.triggerAt != null && progress < 1;
  const sweepDeg = progress * 360;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isSupported || disabled}
      title={tooltipTitle}
      aria-label={tooltipTitle}
      className={`${baseClasses} ${stateClasses}`}
    >
      {isListening && (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-xl bg-red-500 opacity-75 animate-ping"
        />
      )}
      {showCooldown && (
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `conic-gradient(from 0deg, transparent ${sweepDeg}deg, rgba(0, 0, 0, 0.45) ${sweepDeg}deg)`,
          }}
        />
      )}
      <span className="relative">
        {isListening ? (
          <MdStop size={20} />
        ) : isSupported ? (
          <MdMic size={20} />
        ) : (
          <MdMicOff size={20} />
        )}
      </span>
    </button>
  );
};

export default MicrophoneButton;
